 <title>Systematizing SQL Change Management</title>

  <h1>Systematizing SQL Change Management – Database DevOps Done Right</h1>

  <p>You’d never design a code deployment process filled with manual steps, last-minute edits, untraceable changes, and unclear history. Yet this is still common when managing database changes.</p>

  <p>A modern, reliable approach to database DevOps applies the same principles used for application code: version control, repeatability, traceability, and automation.</p>

  <div class="step">
    <h2>1. Script the Change</h2>
    <p>Developers write SQL scripts to modify database objects. Scripts must be <em>idempotent</em>—producing the same results even if run multiple times.</p>
  </div>

  <div class="step">
    <h2>2. Test Locally</h2>
    <p>Each script is applied and tested against a local development copy of the database.</p>
  </div>

  <div class="step">
    <h2>3. Version the Script</h2>
    <p>Scripts are saved to clearly named files that reflect the database object—e.g., <code>Database/Tables/Customers.sql</code>. When an object changes, the same file is overwritten to preserve Git history.</p>
  </div>

  <blockquote>
    By updating the same file, the Git history <strong>is</strong> the change history of the database object.
  </blockquote>

  <div class="step">
    <h2>4. Commit with Traceability</h2>
    <p>The file is committed to Git along with a required change ticket number for traceability.</p>
  </div>

  <div class="step">
    <h2>5. Build & Test in CI/CD</h2>
    <p>When changes are pushed to a branch, a pipeline:</p>
    <ul>
      <li>Builds a single release script using <strong>GitToolset</strong></li>
      <li>Restores a clean copy of the database</li>
      <li>Applies the script</li>
      <li>Runs automated tests</li>
    </ul>
    <p>If any step fails, the commit fails.</p>
  </div>

  <div class="step">
    <h2>6. GitToolset in Action</h2>
    <p><strong>GitToolset</strong> extracts all SQL scripts changed since the last sprint, sorts them by dependency, and adds Git metadata: file name, author, commit SHA, and timestamp.</p>
  </div>

  <div class="step">
    <h2>7. Prepare for Release</h2>
    <p>After merging to the main branch, GitToolset generates a final release script containing all changes. This script is deployed to the test environment.</p>
  </div>

  <div class="step">
    <h2>8. Iterate as Needed</h2>
    <p>If a script needs changes during QA or later phases, it’s updated and recommitted, triggering a new round of testing and validation.</p>
  </div>

<div class="quote">
  <blockquote>
    <p>
      Automate every database refactoring with SQL DDL for schema changes and DML for data migrations. Test migration scripts on the mainline database, then apply them across QA and Staging. Deploy the same vetted scripts to production for confident, error-free updates.
    </p>
    <cite>
      — <a href="https://en.wikipedia.org/wiki/Martin_Fowler" alt="Fowler">Martin Fowler</a> and <a href="https://www.nofluffjuststuff.com/conference/speaker/pramod_sadalage" alt="Sadalage">Pramod Sadalage</a>, 
      <a href="http://martinfowler.com/articles/evodb.html" alt="EDD" target="_blank">Evolutionary Database Design</a>
    </cite>
  </blockquote>
</div>

<br>
<h2>Next Steps</h2>
<ul>
  <li><a href="/documentation">Explore the Documentation</a> to learn setup and configuration details.</li>
  <li><a href="/product">Obtain a License</a> to unlock GitToolset’s full potential.</li>
  <li><a href="/downloads">Download GitToolset</a> and start integrating it into your workflow.</li>
</ul>
<br><br>